import { useInfiniteQuery } from 'react-query'
import { getJSON } from 'common/services/api'
import { ServiceRecord } from '../types'

interface ServiceRecordsListResponse {
  data: ServiceRecord[]
  pagination: {
    currentPage: number
    itemsPerPage: number
    totalItems: number
    totalPages: number
  }
}

interface UseServiceRecordsListParams {
  residentId: string
  limit?: number
}

interface UseServiceRecordsListReturn {
  serviceRecords: ServiceRecord[]
  isLoading: boolean
  refetch: () => void
  fetchNextPage: () => void
  hasNextPage: boolean
  isFetchingNextPage: boolean
}

export const useServiceRecordsList = ({
  residentId,
  limit = 50
}: UseServiceRecordsListParams): UseServiceRecordsListReturn => {
  const fetchServiceRecords = async ({ pageParam = 1 }) => {
    const url = `/v6/residents/${residentId}/service-records?limit=${limit}&page=${pageParam}`

    const response = (await getJSON({ url })) as ServiceRecordsListResponse

    return {
      serviceRecords: response.data || [],
      totalCount: response.pagination?.totalItems || 0,
      currentPage: response.pagination?.currentPage || pageParam,
      totalPages: response.pagination?.totalPages || 1
    }
  }

  const {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch
  } = useInfiniteQuery(['service-records', residentId], fetchServiceRecords, {
    getNextPageParam: (lastPage, allPages) => {
      const loadedCount = allPages.reduce(
        (total, page) => total + page.serviceRecords.length,
        0
      )

      if (loadedCount >= lastPage.totalCount) {
        return undefined
      }

      if (lastPage.currentPage >= lastPage.totalPages) {
        return undefined
      }

      return lastPage.currentPage + 1
    },
    enabled: !!residentId
  })

  const serviceRecords =
    data?.pages.flatMap((page) => page.serviceRecords) || []

  return {
    serviceRecords,
    isLoading,
    refetch,
    fetchNextPage,
    hasNextPage: !!hasNextPage,
    isFetchingNextPage
  }
}
